export type Meta = {
  errors: Error[];
};
type Store<TFormData> = {
  fields: TFormData;
  meta: {
    [K in keyof TFormData]: Meta;
  };
};
type StoreListener = () => void;
type UnsubscribeListener = () => void;
export type StoreApi<TFormData> = {
  updateStore: (nextPartialStore: Partial<Store<TFormData>>) => void;
  updateFieldValue: (field: keyof TFormData, value: TFormData[keyof TFormData]) => void;

  subscribe: (listener: StoreListener) => UnsubscribeListener;
  getSnapshot: () => Store<TFormData>;
  getServerSnapshot: () => Store<TFormData>;
};

function createMeta<TFormData extends Record<string, unknown>>(
  formDataKeys: (keyof TFormData)[]
): Store<TFormData>['meta'] {
  const result = {} as Store<TFormData>['meta'];

  for (const key of formDataKeys) {
    Object.assign(result, {
      [key]: {
        errors: [],
      },
    });
  }

  return result;
}

/**
 * Form store. Stores local copy from from defaultValues.
 * Contains current field values and errors.
 *
 * @param defaultValues Default values to initialize store
 * @returns Form store reference
 */
export function createStore<TFormData extends Record<string, unknown>>(defaultValues: TFormData): StoreApi<TFormData> {
  let store: Store<TFormData> = {
    fields: defaultValues,
    meta: createMeta(Object.keys(defaultValues)),
  };
  const initialSnapshot = structuredClone(store);
  let listeners: StoreListener[] = [];

  function emitChange(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  const updateStore: StoreApi<TFormData>['updateStore'] = (nextPartialStore: Partial<Store<TFormData>>) => {
    store = {
      ...store,
      ...nextPartialStore,
    };
    emitChange();
  };

  const updateFieldValue: StoreApi<TFormData>['updateFieldValue'] = (name, value) => {
    const nextStore = {
      ...store,
      fields: {
        ...store.fields,
        [name]: {
          value,
        },
      },
    } satisfies Store<TFormData>;

    store = nextStore;
    emitChange();
  };

  const subscribe: StoreApi<TFormData>['subscribe'] = (listener) => {
    listeners = [...listeners, listener];

    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  };

  const getSnapshot: StoreApi<TFormData>['getSnapshot'] = () => {
    return store;
  };

  const getServerSnapshot: StoreApi<TFormData>['getServerSnapshot'] = () => {
    return initialSnapshot;
  };

  const storeApi = {
    updateStore,
    updateFieldValue,
    subscribe,
    getSnapshot,
    getServerSnapshot,
  } as const;

  return storeApi;
}
